import { useCallback, useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectMultipleObjects,
  deselectObject,
  setDrawMode,
  setRectangleDrawing,
  setRectangleStart,
  setRectangleCurrent,
  clearRectangleUI,
  isDrawMode,
  isSelectionModeActive,
  getSelectedObjects,
  getSelectionTool,
  setSelectionTool,
  selectionTools,
} from "../../redux/objectSelection";
import * as THREE from "three";
import { getHasActiveFilters } from "../CustomHooks";
import { isWindowPreviouslySelected } from "../utils";

const Proper3DRectSelector = () => {
  const isDrawActive = useSelector((state) => isDrawMode(state));
  const isSelectionActive = useSelector((state) =>
    isSelectionModeActive(state, "windows")
  );
  const selectionTool = useSelector((state) => getSelectionTool(state));
  const { camera, gl } = useThree();
  const dispatch = useDispatch();

  const [localDrawing, setLocalDrawing] = useState({
    isDrawing: false,
    startPoint: null,
    currentPoint: null,
  });

  const [lastReduxUpdate, setLastReduxUpdate] = useState(0);

  const allWindows = useSelector((state) => state.windowData.allWindows);
  const filteredWindows = useSelector(
    (state) => state.windowData.filteredWindows
  );
  const windowFilters = useSelector((state) => state.windowData.filters);
  const resultArray = useSelector((state) => state.windowData.resultArray);
  const preRayConfigurations = useSelector(
    (state) => state.sensorAnalysis.preRay.configurations
  );
  const selectedFloor = useSelector(
    (state) => state.floorVisibility.selectedFloors
  );

  const selectedObjects = useSelector((state) => getSelectedObjects(state));

  const hasActiveFilters = getHasActiveFilters(windowFilters);

  const visibleWindows = hasActiveFilters
    ? filteredWindows
    : allWindows.filter((window) => {
        for (const floorIndex in resultArray) {
          const floor = resultArray[floorIndex];
          if (selectedFloor[floorIndex]) {
            for (const windowIndex in floor) {
              const floorWindow = floor[windowIndex];
              if (window.id === floorWindow.id) {
                return true;
              }
            }
          }
        }
        return false;
      });

  const worldToScreen = useCallback(
    (worldPosition) => {
      const vector = new THREE.Vector3();
      vector.copy(worldPosition);

      const rotatedVector = vector.clone();
      rotatedVector.applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI * -0.5);

      rotatedVector.project(camera);

      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();

      const x = (rotatedVector.x * 0.5 + 0.5) * rect.width;
      const y = (-rotatedVector.y * 0.5 + 0.5) * rect.height;

      return {
        x: x + rect.left,
        y: y + rect.top,
        z: rotatedVector.z,
      };
    },
    [camera, gl]
  );

  const getWindowWorldPosition = useCallback((window) => {
    try {
      const polygon =
        typeof window.polygon === "string"
          ? JSON.parse(window.polygon)
          : window.polygon;

      if (!polygon || polygon.length === 0) return null;

      let centerX = 0,
        centerY = 0,
        centerZ = 0;
      const vertexCount = polygon.length / 3;

      for (let i = 0; i < polygon.length; i += 3) {
        centerX += polygon[i];
        centerY += polygon[i + 1];
        centerZ += polygon[i + 2];
      }

      return new THREE.Vector3(
        centerX / vertexCount,
        centerY / vertexCount,
        centerZ / vertexCount
      );
    } catch (error) {
      return null;
    }
  }, []);

  const isPointInRect = useCallback((point, rectStart, rectEnd) => {
    const minX = Math.min(rectStart.x, rectEnd.x);
    const maxX = Math.max(rectStart.x, rectEnd.x);
    const minY = Math.min(rectStart.y, rectEnd.y);
    const maxY = Math.max(rectStart.y, rectEnd.y);

    return (
      point.x >= minX &&
      point.x <= maxX &&
      point.y >= minY &&
      point.y <= maxY &&
      point.z > -1 &&
      point.z < 1
    );
  }, []);

  useEffect(() => {
    if (!isDrawActive || !isSelectionActive || !gl.domElement) return;

    const canvas = gl.domElement;

    const handleMouseDown = (e) => {
      if (!isDrawActive || !isSelectionActive) return;
      e.preventDefault();
      e.stopPropagation();

      const point = { x: e.clientX, y: e.clientY };

      setLocalDrawing({
        isDrawing: true,
        startPoint: point,
        currentPoint: point,
      });

      dispatch(setRectangleDrawing(true));
      dispatch(setRectangleStart(point));
      dispatch(setRectangleCurrent(point));
    };

    const handleMouseMove = (e) => {
      if (!isDrawActive || !isSelectionActive || !localDrawing.isDrawing)
        return;
      e.preventDefault();
      e.stopPropagation();

      const point = { x: e.clientX, y: e.clientY };

      setLocalDrawing((prev) => ({
        ...prev,
        currentPoint: point,
      }));

      const now = Date.now();
      if (now - lastReduxUpdate > 4) {
        dispatch(setRectangleCurrent(point));
        setLastReduxUpdate(now);
      }
    };

    const handleMouseUp = (e) => {
      if (
        !isDrawActive ||
        !isSelectionActive ||
        !localDrawing.isDrawing ||
        !localDrawing.startPoint
      )
        return;
      e.preventDefault();
      e.stopPropagation();

      dispatch(clearRectangleUI());
      setLocalDrawing({
        isDrawing: false,
        startPoint: null,
        currentPoint: null,
      });

      const endPoint = { x: e.clientX, y: e.clientY };
      const startPoint = localDrawing.startPoint;

      const width = Math.abs(endPoint.x - startPoint.x);
      const height = Math.abs(endPoint.y - startPoint.y);

      if (width < 10 || height < 10) return;

      const windowIdsInRect = [];

      visibleWindows.forEach((window) => {
        if (isWindowPreviouslySelected(window.id, preRayConfigurations)) return;
        const worldPos = getWindowWorldPosition(window);
        if (worldPos) {
          const screenPos = worldToScreen(worldPos);
          if (isPointInRect(screenPos, startPoint, endPoint)) {
            windowIdsInRect.push(window.id);
          }
        }
      });

      if (windowIdsInRect.length > 0) {
        if (selectionTool === selectionTools.ERASE) {
          windowIdsInRect.forEach((windowId) => {
            if (selectedObjects.includes(windowId)) {
              dispatch(deselectObject({ objectId: windowId }));
            }
          });
        } else {
          dispatch(
            selectMultipleObjects({
              objectIds: windowIdsInRect,
            })
          );
        }
      }

      setTimeout(() => {
        dispatch(setDrawMode(false));
        dispatch(setSelectionTool(selectionTools.SINGLE));
      }, 30);
    };

    canvas.addEventListener("mousedown", handleMouseDown, { capture: true });
    canvas.addEventListener("mousemove", handleMouseMove, { capture: true });
    canvas.addEventListener("mouseup", handleMouseUp, { capture: true });
    canvas.addEventListener("mouseleave", handleMouseUp, { capture: true });

    const originalCursor = canvas.style.cursor;
    canvas.style.cursor = "crosshair";

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown, {
        capture: true,
      });
      canvas.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      canvas.removeEventListener("mouseup", handleMouseUp, { capture: true });
      canvas.removeEventListener("mouseleave", handleMouseUp, {
        capture: true,
      });

      canvas.style.cursor = originalCursor;
    };
  }, [
    isDrawActive,
    isSelectionActive,
    localDrawing.isDrawing,
    localDrawing.startPoint,
    visibleWindows,
    getWindowWorldPosition,
    worldToScreen,
    isPointInRect,
    dispatch,
    gl,
    lastReduxUpdate,
    selectionTool,
    selectedObjects,
    preRayConfigurations,
  ]);

  return null;
};

export default Proper3DRectSelector;
